import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon, PlusIcon, DocumentTextIcon, ClipboardDocumentIcon,
  ArrowPathIcon, ArrowDownTrayIcon, BuildingOfficeIcon, ChevronRightIcon,
  ArrowLeftIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { documentalApi } from '../../api/documental';
import { empresasApi } from '../../api/empresas';
import NuevoExpediente from './NuevoExpediente';

const ESTADO_BADGE = {
  borrador: 'bg-gray-100 text-gray-600',
  pendiente_cliente: 'bg-yellow-100 text-yellow-700',
  confirmado_cliente: 'bg-blue-100 text-blue-700',
  generado: 'bg-green-100 text-green-700',
};

const ESTADO_LABEL = {
  borrador: 'Borrador',
  pendiente_cliente: 'Pendiente cliente',
  confirmado_cliente: 'Confirmado',
  generado: 'Generado',
};

export default function GestionDocumental() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const empresaIdFromUrl = params.get('empresa_id');

  // --- Empresa selector ---
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [buscarEmpresa, setBuscarEmpresa] = useState('');
  const [resultadosEmpresa, setResultadosEmpresa] = useState([]);
  const [buscandoEmpresa, setBuscandoEmpresa] = useState(false);

  // --- Expedientes ---
  const [expedientes, setExpedientes] = useState([]);
  const [loadingExp, setLoadingExp] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [selectedTipo, setSelectedTipo] = useState(null);

  // --- Detalle expediente ---
  const [expedienteActivo, setExpedienteActivo] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [pdfActivo, setPdfActivo] = useState(null);

  // --- Búsqueda de empresa ---
  const [buscarError, setBuscarError] = useState('');
  const [buscarRealizado, setBuscarRealizado] = useState(false);

  // --- Acciones ---
  const [copiedId, setCopiedId] = useState(null);
  const [regenId, setRegenId] = useState(null);
  const [error, setError] = useState('');

  // Cargar empresa desde URL
  useEffect(() => {
    if (empresaIdFromUrl) {
      empresasApi.obtener(empresaIdFromUrl)
        .then(setEmpresaSeleccionada)
        .catch(() => setEmpresaSeleccionada(null));
    }
  }, [empresaIdFromUrl]);

  // Buscar empresas
  useEffect(() => {
    if (!buscarEmpresa.trim()) {
      setResultadosEmpresa([]);
      setBuscarRealizado(false);
      setBuscarError('');
      return;
    }
    const t = setTimeout(async () => {
      setBuscandoEmpresa(true);
      setBuscarError('');
      try {
        const res = await empresasApi.buscar(buscarEmpresa);
        setResultadosEmpresa(Array.isArray(res) ? res : []);
        setBuscarRealizado(true);
      } catch (e) {
        setResultadosEmpresa([]);
        setBuscarError(e?.response?.data?.detail || 'Error al buscar. Verifica la conexión.');
        setBuscarRealizado(true);
      } finally {
        setBuscandoEmpresa(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [buscarEmpresa]);

  const seleccionarEmpresa = (emp) => {
    setEmpresaSeleccionada(emp);
    setBuscarEmpresa('');
    setResultadosEmpresa([]);
    setBuscarRealizado(false);
    setBuscarError('');
    navigate(`/admin/documental?empresa_id=${emp.id}`, { replace: true });
  };

  const limpiarEmpresa = () => {
    setEmpresaSeleccionada(null);
    setExpedientes([]);
    setExpedienteActivo(null);
    setBuscarRealizado(false);
    setBuscarError('');
    navigate('/admin/documental', { replace: true });
  };

  // Cargar tipos + expedientes
  const cargarDatos = useCallback(async () => {
    setLoadingExp(true);
    setError('');
    try {
      const [tiposRes, expRes] = await Promise.all([
        documentalApi.getTiposTramite(),
        documentalApi.getExpedientes(
          empresaSeleccionada ? { empresa_id: empresaSeleccionada.id } : {}
        ),
      ]);
      setTipos(Array.isArray(tiposRes) ? tiposRes : []);
      setExpedientes(Array.isArray(expRes) ? expRes : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando datos');
    } finally {
      setLoadingExp(false);
    }
  }, [empresaSeleccionada]);

  useEffect(() => {
    if (empresaSeleccionada) cargarDatos();
  }, [cargarDatos, empresaSeleccionada]);

  // Ver documentos de expediente
  const verExpediente = async (exp) => {
    setExpedienteActivo(exp);
    setPdfActivo(null);
    setLoadingDocs(true);
    try {
      const docs = await documentalApi.getDocumentos(exp.id);
      const lista = Array.isArray(docs) ? docs : [];
      setDocumentos(lista);
      const primerPdf = lista.find((d) => d.archivo_pdf_url);
      if (primerPdf) setPdfActivo(primerPdf.archivo_pdf_url);
    } catch { setDocumentos([]); }
    finally { setLoadingDocs(false); }
  };

  const copiarLink = async (exp) => {
    try {
      await navigator.clipboard.writeText(exp.link_cliente_url || '');
      setCopiedId(exp.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* ignore */ }
  };

  const regenerarLink = async (exp) => {
    setRegenId(exp.id);
    try {
      await documentalApi.regenerarLinkCliente(exp.id);
      await cargarDatos();
      if (expedienteActivo?.id === exp.id) {
        const actualizado = expedientes.find((e) => e.id === exp.id);
        if (actualizado) setExpedienteActivo(actualizado);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al regenerar link');
    } finally {
      setRegenId(null);
    }
  };

  // ─── Render: Sin empresa seleccionada ─────────────────────────────────────
  if (!empresaSeleccionada) {
    return (
      <div className="max-w-2xl mx-auto mt-12 px-4">
        <div className="text-center mb-8">
          <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-3" style={{ color: '#12284C' }} />
          <h1 className="text-3xl font-bold mt-2" style={{ color: '#1e293b' }}>
            Gestión Documental
          </h1>
          <p className="mt-2 text-base" style={{ color: '#64748b' }}>
            Selecciona una empresa para gestionar sus expedientes y documentos
          </p>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <label className="block text-sm font-semibold mb-2" style={{ color: '#374151' }}>
            Buscar empresa por nombre, RNC o actividad
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              className="pl-9 pr-3 py-2.5 w-full border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
              placeholder="Escribe para buscar..."
              value={buscarEmpresa}
              onChange={(e) => setBuscarEmpresa(e.target.value)}
              autoFocus
            />
          </div>

          {buscandoEmpresa && (
            <p className="text-xs text-gray-400 mt-2 animate-pulse">Buscando...</p>
          )}

          {buscarError && (
            <p className="text-xs text-red-500 mt-2">{buscarError}</p>
          )}

          {resultadosEmpresa.length > 0 && (
            <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
              {resultadosEmpresa.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => seleccionarEmpresa(emp)}
                  className="flex items-center justify-between w-full px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{emp.nombre}</p>
                    <p className="text-xs text-gray-500">{emp.rnc || 'Sin RNC'} · {emp.actividad_economica || '—'}</p>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {buscarRealizado && !buscandoEmpresa && resultadosEmpresa.length === 0 && !buscarError && buscarEmpresa.trim() && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-4 text-center">
              <p className="text-sm text-amber-800 font-medium">
                No se encontraron empresas con ese criterio.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Primero debes registrar las empresas en el módulo de Gestión de Empresas.
              </p>
              <a
                href="/admin/gestion-empresas"
                className="inline-block mt-2 text-sm font-semibold text-blue-600 hover:underline"
              >
                Ir a Gestión de Empresas →
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Render: Con empresa + detalle expediente ──────────────────────────────
  if (expedienteActivo) {
    return (
      <div className="h-full flex flex-col">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
          <button onClick={limpiarEmpresa} className="hover:text-primary-600">Documental</button>
          <ChevronRightIcon className="h-3.5 w-3.5" />
          <button onClick={() => setExpedienteActivo(null)} className="hover:text-primary-600">{empresaSeleccionada.nombre}</button>
          <ChevronRightIcon className="h-3.5 w-3.5" />
          <span className="text-gray-700 font-medium truncate">{expedienteActivo.titulo || expedienteActivo.empresa_nombre}</span>
        </div>

        {/* Expediente header */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {expedienteActivo.titulo || expedienteActivo.empresa_nombre}
            </h2>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span>{expedienteActivo.tipo_tramite_nombre}</span>
              <span>·</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${ESTADO_BADGE[expedienteActivo.estado] || ''}`}>
                {ESTADO_LABEL[expedienteActivo.estado] || expedienteActivo.estado}
              </span>
              <span>·</span>
              <span>{new Date(expedienteActivo.fecha_creacion).toLocaleDateString('es-DO')}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => copiarLink(expedienteActivo)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              {copiedId === expedienteActivo.id ? 'Copiado' : 'Copiar link cliente'}
            </button>
            <button
              onClick={() => regenerarLink(expedienteActivo)}
              disabled={regenId === expedienteActivo.id}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-amber-300 rounded-lg hover:bg-amber-50 text-amber-700 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              {regenId === expedienteActivo.id ? 'Regenerando...' : 'Regenerar link'}
            </button>
            {expedienteActivo.link_cliente_url && (
              <a
                href={expedienteActivo.link_cliente_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-xs border border-primary-300 rounded-lg hover:bg-primary-50 text-primary-700"
              >
                <EyeIcon className="h-3.5 w-3.5" />
                Vista cliente
              </a>
            )}
          </div>
        </div>

        {/* Panel dividido: izquierda datos, derecha PDF */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
          {/* Izquierda: Datos y documentos */}
          <div className="space-y-4 overflow-y-auto">
            {/* Datos base */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Datos del expediente</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <dt className="text-gray-500">Empresa</dt>
                  <dd className="font-medium text-gray-900">{expedienteActivo.empresa_nombre}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">RNC</dt>
                  <dd className="font-medium text-gray-900">{expedienteActivo.empresa_rnc || '—'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-gray-500">Link cliente</dt>
                  <dd className="font-mono text-gray-700 truncate">{expedienteActivo.link_cliente_url}</dd>
                  {expedienteActivo.link_cliente_expirado && (
                    <p className="text-red-500 text-xs mt-0.5">Link expirado — regenerar para reactivar</p>
                  )}
                </div>
                {expedienteActivo.cambios_confirmados_cliente && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Confirmado por cliente</dt>
                    <dd className="text-green-600 font-medium">
                      {new Date(expedienteActivo.fecha_confirmacion_cliente).toLocaleString('es-DO')}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Documentos generados */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Documentos generados</h3>
              {loadingDocs ? (
                <p className="text-xs text-gray-400">Cargando...</p>
              ) : documentos.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Sin documentos generados aún</p>
              ) : (
                <div className="space-y-2">
                  {documentos.map((doc) => (
                    <div
                      key={doc.id}
                      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${pdfActivo === doc.archivo_pdf_url ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => doc.archivo_pdf_url && setPdfActivo(doc.archivo_pdf_url)}
                    >
                      <div>
                        <p className="text-xs font-medium text-gray-900">{doc.plantilla_nombre}</p>
                        <p className="text-xs text-gray-500">{new Date(doc.fecha_generacion).toLocaleDateString('es-DO')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.archivo_pdf_url && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setPdfActivo(doc.archivo_pdf_url); }}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            Ver PDF
                          </button>
                        )}
                        {doc.archivo_url && (
                          <a href={doc.archivo_url} onClick={(e) => e.stopPropagation()} className="text-xs text-gray-500 hover:underline flex items-center gap-0.5">
                            <ArrowDownTrayIcon className="h-3 w-3" /> DOCX
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Datos cambios */}
            {expedienteActivo.datos_cambios && Object.keys(expedienteActivo.datos_cambios).length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Cambios del cliente</h3>
                <dl className="space-y-1.5 text-xs">
                  {Object.entries(expedienteActivo.datos_cambios)
                    .filter(([k]) => !['orden_dia', 'socios_modificados'].includes(k))
                    .map(([k, v]) => v ? (
                      <div key={k} className="grid grid-cols-2 gap-2">
                        <dt className="text-gray-500 truncate">{k.replaceAll('_', ' ').toLowerCase()}</dt>
                        <dd className="font-medium text-gray-900 truncate">{String(v)}</dd>
                      </div>
                    ) : null)}
                </dl>
              </div>
            )}
          </div>

          {/* Derecha: PDF Viewer */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col min-h-96 lg:min-h-0">
            {pdfActivo ? (
              <iframe
                src={pdfActivo}
                className="flex-1 w-full"
                title="Vista previa PDF"
                style={{ minHeight: '500px' }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">
                    {documentos.length > 0
                      ? 'Selecciona un documento para ver su PDF'
                      : 'No hay documentos generados para este expediente'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Empresa seleccionada, lista de expedientes ───────────────────
  return (
    <div>
      {/* Header empresa */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={limpiarEmpresa}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{empresaSeleccionada.nombre}</h1>
            <p className="text-xs text-gray-500">
              {empresaSeleccionada.rnc && <span>RNC: {empresaSeleccionada.rnc} · </span>}
              {empresaSeleccionada.actividad_economica}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Cambiar empresa */}
          <div className="relative">
            <div className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400" />
              <input
                className="bg-transparent text-gray-700 outline-none w-40"
                placeholder="Cambiar empresa..."
                value={buscarEmpresa}
                onChange={(e) => setBuscarEmpresa(e.target.value)}
              />
            </div>
            {resultadosEmpresa.length > 0 && (
              <div className="absolute top-full right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                {resultadosEmpresa.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => seleccionarEmpresa(emp)}
                    className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{emp.nombre}</p>
                      <p className="text-xs text-gray-500">{emp.rnc || '—'}</p>
                    </div>
                    <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setSelectedTipo('picker')}
            className="flex items-center gap-1 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
          >
            <PlusIcon className="h-4 w-4" />
            Nuevo expediente
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
          <button className="ml-2 underline" onClick={() => setError('')}>Cerrar</button>
        </div>
      )}

      {/* Picker de tipo de trámite */}
      {selectedTipo === 'picker' && (
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Selecciona el tipo de trámite</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tipos.map((tipo) => (
              <button
                key={tipo.id}
                onClick={() => setSelectedTipo(tipo)}
                className="text-left border border-gray-200 rounded-lg p-3 hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <p className="font-medium text-sm text-gray-900">{tipo.nombre}</p>
                <p className="text-xs text-gray-500 mt-1">{tipo.descripcion || 'Sin descripción'}</p>
                <p className="text-xs text-gray-400 mt-1">{(tipo.plantillas || []).length} plantilla(s)</p>
              </button>
            ))}
          </div>
          <button onClick={() => setSelectedTipo(null)} className="mt-3 text-xs text-gray-400 hover:text-gray-600">Cancelar</button>
        </div>
      )}

      {/* Lista de expedientes */}
      {loadingExp ? (
        <div className="text-center py-12 text-gray-400">Cargando expedientes...</div>
      ) : expedientes.length === 0 ? (
        <div className="text-center py-16">
          <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            No hay expedientes para esta empresa.
          </p>
          <button
            onClick={() => setSelectedTipo('picker')}
            className="mt-3 text-primary-600 hover:underline text-sm"
          >
            Crear primer expediente
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {expedientes.map((exp) => (
            <div
              key={exp.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {exp.titulo || exp.empresa_nombre}
                    </h3>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_BADGE[exp.estado] || ''}`}>
                      {ESTADO_LABEL[exp.estado] || exp.estado}
                    </span>
                    {exp.cambios_confirmados_cliente && (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        Cliente confirmó
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {exp.tipo_tramite_nombre} · {new Date(exp.fecha_creacion).toLocaleDateString('es-DO')}
                    {exp.link_cliente_expirado && <span className="ml-2 text-red-500">Link expirado</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => verExpediente(exp)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                  >
                    <EyeIcon className="h-3.5 w-3.5" />
                    Ver detalle
                  </button>
                  <button
                    onClick={() => copiarLink(exp)}
                    title="Copiar link cliente"
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => regenerarLink(exp)}
                    disabled={regenId === exp.id}
                    title="Regenerar link"
                    className="p-1.5 rounded-lg border border-amber-200 hover:bg-amber-50 text-amber-600 disabled:opacity-50"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal nuevo expediente */}
      {selectedTipo && selectedTipo !== 'picker' && (
        <NuevoExpediente
          tipoTramite={selectedTipo}
          empresaId={empresaSeleccionada?.id}
          onClose={() => setSelectedTipo(null)}
          onCreated={() => { setSelectedTipo(null); cargarDatos(); }}
        />
      )}
    </div>
  );
}
